import { ArrowRight, Check, Quote, Calendar, Smartphone, Wrench } from "lucide-react";
import ConceptHeader from "./ConceptHeader";
import { ConceptFooter } from "./LayoutServicePro";

/**
 * "Modern Tech" layout — light, app-feel, plan/tier focused.
 * Used by BlueWave HVAC.
 * Distinct sections: gradient hero w/ image right + floating booking-card,
 * 3-step "How it works" diagram, membership pricing tiers, services grid,
 * mobile-app preview, testimonial.
 */
export default function LayoutModernTech({ c }) {
  const t = c.theme;
  return (
    <div style={{ background: t.bg, color: t.text, fontFamily: t.fontBody, minHeight: "100vh" }}>
      <ConceptHeader industry={c.industry} />

      {/* Faux site nav */}
      <div className="max-w-7xl mx-auto px-6 md:px-12 pt-8 flex items-center justify-between">
        <p style={{ fontFamily: t.fontDisplay, fontSize: 26 }}>{c.name}</p>
        <nav className="hidden md:flex items-center gap-7 text-[13px]" style={{ color: t.muted }}>
          <span>Services</span><span>Comfort Club</span><span>About</span><span>Reviews</span>
          <span className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full font-medium"
                style={{ background: t.accent, color: "#fff" }}>
            {c.hero.ctaPrimary}
          </span>
        </nav>
      </div>

      {/* HERO — gradient, image right, floating booking card */}
      <section className="relative overflow-hidden" style={{ background: t.heroBg }}>
        <div className="absolute -top-32 -right-32 w-[600px] h-[600px] rounded-full opacity-30"
             style={{ background: t.accent }}/>
        <div className="max-w-7xl mx-auto px-6 md:px-12 pt-16 pb-24 md:pb-32 grid lg:grid-cols-12 gap-10 items-center relative">
          <div className="lg:col-span-6">
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[12px] font-medium"
                  style={{ background: t.accentSoft, color: t.accent }}>
              <span className="w-2 h-2 rounded-full" style={{ background: t.accent }}/>
              {c.hero.eyebrow}
            </span>
            <h1 className="mt-7 leading-[1.04]"
                style={{ fontFamily: t.fontDisplay, fontSize: "clamp(42px, 6vw, 80px)", fontWeight: 600 }}>
              {c.hero.title}
            </h1>
            <p className="mt-7 text-lg leading-relaxed max-w-xl" style={{ color: t.muted }}>{c.hero.sub}</p>
            <div className="mt-9 flex flex-wrap gap-3">
              <span className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full font-medium text-[15px]"
                    style={{ background: t.accent, color: "#fff" }}>
                {c.hero.ctaPrimary} <ArrowRight size={15}/>
              </span>
              <span className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full font-medium text-[15px] border"
                    style={{ borderColor: t.muted + "55", color: t.text, background: "#fff" }}>
                {c.hero.ctaSecondary}
              </span>
            </div>
          </div>
          <div className="lg:col-span-6 relative">
            <div className="aspect-[4/3] rounded-3xl overflow-hidden"
                 style={{ background: `url(${c.images.hero}) center/cover` }}/>
            {/* floating card */}
            <div className="absolute -bottom-8 -left-6 hidden md:block w-[300px] p-5 rounded-2xl border bg-white"
                 style={{ borderColor: t.muted + "22", boxShadow: "0 18px 48px -20px rgba(0,0,0,0.16)" }}>
              <div className="flex items-center gap-3">
                <span className="inline-flex items-center justify-center w-10 h-10 rounded-full"
                      style={{ background: t.accentSoft, color: t.accent }}>
                  <Calendar size={18}/>
                </span>
                <div>
                  <p className="text-[11px] uppercase tracking-[0.22em]" style={{ color: t.muted }}>Today</p>
                  <p style={{ fontFamily: t.fontDisplay, fontSize: 18 }}>2 of 5 slots left</p>
                </div>
              </div>
              <div className="mt-4 grid grid-cols-3 gap-1.5">
                {["9 AM", "11 AM", "1 PM", "3 PM", "5 PM"].map((slot, i) => (
                  <span key={slot} className="text-center py-1.5 rounded-md text-[12px]"
                        style={{
                          background: i === 1 ? t.accent : t.bg,
                          color: i === 1 ? "#fff" : t.text,
                          border: `1px solid ${i === 1 ? t.accent : t.muted + "22"}`,
                        }}>
                    {slot}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* STATS strip */}
      <section className="py-12 border-y" style={{ background: t.surface, borderColor: t.muted + "22" }}>
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-2 md:grid-cols-4 gap-8">
          {c.stats.map((s) => (
            <div key={s.v} className="text-center md:text-left">
              <p style={{ fontFamily: t.fontDisplay, fontSize: 32, color: t.accent, fontWeight: 600 }}>{s.k}</p>
              <p className="mt-1 text-[12px] uppercase tracking-[0.22em]" style={{ color: t.muted }}>{s.v}</p>
            </div>
          ))}
        </div>
      </section>

      {/* HOW IT WORKS — 3 step diagram */}
      <section className="py-24 md:py-28">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-2xl">
            <p className="text-[12px] uppercase tracking-[0.22em]" style={{ color: t.accent }}>How it works</p>
            <h2 className="mt-4 leading-tight" style={{ fontFamily: t.fontDisplay, fontSize: "clamp(32px, 4vw, 52px)", fontWeight: 600 }}>
              Comfort in three taps.
            </h2>
          </div>
          <div className="mt-14 grid md:grid-cols-3 gap-6 relative">
            {/* dotted line */}
            <div className="absolute top-8 left-[16%] right-[16%] h-px hidden md:block"
                 style={{ borderTop: `2px dashed ${t.accent}55` }}/>
            {c.steps.map((s) => (
              <div key={s.n} className="relative p-7 rounded-2xl border bg-white" style={{ borderColor: t.muted + "22" }}>
                <span className="inline-flex items-center justify-center w-16 h-16 rounded-full text-[15px] font-mono font-bold absolute -top-8 left-7"
                      style={{ background: t.accent, color: "#fff" }}>
                  {s.n}
                </span>
                <h3 className="mt-8 leading-tight" style={{ fontFamily: t.fontDisplay, fontSize: 24, fontWeight: 600 }}>{s.title}</h3>
                <p className="mt-3 text-[14px] leading-relaxed" style={{ color: t.muted }}>{s.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PRICING TIERS — Comfort Club */}
      <section className="py-24 md:py-28" style={{ background: t.surface }}>
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <p className="text-[12px] uppercase tracking-[0.22em]" style={{ color: t.accent }}>Comfort Club</p>
            <h2 className="mt-4 leading-tight" style={{ fontFamily: t.fontDisplay, fontSize: "clamp(32px, 4vw, 52px)", fontWeight: 600 }}>
              Membership that pays for itself.
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-5">
            {(c.tiers || []).map((tier) => (
              <div key={tier.name}
                   className={`p-8 rounded-2xl border ${tier.featured ? "scale-[1.02]" : ""}`}
                   style={{
                     background: tier.featured ? t.bg : "#fff",
                     borderColor: tier.featured ? t.accent : t.muted + "22",
                     boxShadow: tier.featured ? `0 20px 50px -25px ${t.accent}66` : "none",
                   }}>
                {tier.featured && (
                  <span className="inline-block text-[11px] uppercase tracking-[0.22em] font-bold mb-3 px-3 py-1 rounded-full"
                        style={{ background: t.accent, color: "#fff" }}>
                    Most popular
                  </span>
                )}
                <h3 style={{ fontFamily: t.fontDisplay, fontSize: 24, fontWeight: 600 }}>{tier.name}</h3>
                <p className="mt-3 flex items-baseline gap-1">
                  <span style={{ fontFamily: t.fontDisplay, fontSize: 48, color: t.accent, fontWeight: 700 }}>{tier.price}</span>
                  <span className="text-[14px]" style={{ color: t.muted }}>{tier.per}</span>
                </p>
                <ul className="mt-6 space-y-3">
                  {tier.features.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-[14px]">
                      <Check size={16} style={{ color: t.accent }} className="mt-0.5 shrink-0"/>
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-7 py-3 rounded-full text-center font-medium text-[14px]"
                     style={{
                       background: tier.featured ? t.accent : "transparent",
                       color: tier.featured ? "#fff" : t.text,
                       border: tier.featured ? "none" : `1px solid ${t.text}`,
                     }}>
                  Join {tier.name}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICES grid */}
      <section className="py-24 md:py-28">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-12 gap-10 mb-14">
            <div className="lg:col-span-6">
              <p className="text-[12px] uppercase tracking-[0.22em]" style={{ color: t.accent }}>Services</p>
              <h2 className="mt-4 leading-tight" style={{ fontFamily: t.fontDisplay, fontSize: "clamp(32px, 4vw, 52px)", fontWeight: 600 }}>
                Everything HVAC, none of the hassle.
              </h2>
            </div>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {c.services.map((s, i) => (
              <div key={s.title} className="p-7 rounded-2xl border bg-white" style={{ borderColor: t.muted + "22" }}>
                <span className="inline-flex items-center justify-center w-10 h-10 rounded-full"
                      style={{ background: t.accentSoft, color: t.accent }}>
                  <Wrench size={16}/>
                </span>
                <h3 className="mt-5 leading-tight" style={{ fontFamily: t.fontDisplay, fontSize: 20, fontWeight: 600 }}>{s.title}</h3>
                <p className="mt-3 text-[14px] leading-relaxed" style={{ color: t.muted }}>{s.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* APP CTA / TESTIMONIAL */}
      <section className="py-24 md:py-28" style={{ background: t.surface }}>
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5">
            <Smartphone size={24} style={{ color: t.accent }}/>
            <p className="mt-5 leading-tight" style={{ fontFamily: t.fontDisplay, fontSize: "clamp(28px, 3.6vw, 44px)", fontWeight: 600 }}>
              Book, track, and pay — from your pocket.
            </p>
            <p className="mt-5 text-lg leading-relaxed" style={{ color: t.muted }}>
              Our app gives Comfort Club members real-time tech tracking, one-tap rebooking, and paperless invoices.
            </p>
          </div>
          <div className="lg:col-span-7">
            <div className="p-8 rounded-2xl border" style={{ background: t.bg, borderColor: t.muted + "22" }}>
              <Quote size={28} style={{ color: t.accent }}/>
              <p className="mt-5 leading-tight" style={{ fontFamily: t.fontDisplay, fontSize: "clamp(20px, 2.4vw, 28px)", fontWeight: 500 }}>
                "{c.testimonial.quote}"
              </p>
              <p className="mt-6 text-[13px] uppercase tracking-[0.22em]" style={{ color: t.muted }}>— {c.testimonial.author}</p>
            </div>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-24" style={{ background: t.heroBg }}>
        <div className="max-w-3xl mx-auto px-6 md:px-12 text-center">
          <h2 className="leading-tight" style={{ fontFamily: t.fontDisplay, fontSize: "clamp(32px, 4vw, 56px)", fontWeight: 600 }}>
            {c.finalCta}.
          </h2>
          <div className="mt-10 flex justify-center">
            <span className="inline-flex items-center gap-2 px-9 py-4 rounded-full font-medium text-[15px]"
                  style={{ background: t.accent, color: "#fff" }}>
              {c.hero.ctaPrimary} <ArrowRight size={16}/>
            </span>
          </div>
        </div>
      </section>

      <ConceptFooter accent={t.accent} muted={t.muted} bg={t.bg}/>
    </div>
  );
}
